import React from "react";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Rating from "@mui/material/Rating";
import axios from "axios";
import { API_post_file_upload_with_eagerly_generated_presigned_url } from "../../../constants";
import { getDefaultAxiosConfig } from "../requests/utilities/requests";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import AutosizingTextarea from "./AutosizingTextarea";

function InputEntry(props) {
  // When props.entry is provided the component edits that entry (values only,
  // attachments are left untouched) instead of creating a new one.
  const isEditMode = Boolean(props.entry);
  const [entryData, setEntryData] = React.useState({
    value_text: props.entry?.value_text ?? "",
    value_integer: props.entry?.value_integer ?? "",
    value_float: props.entry?.value_float ?? "",
    file_upload_uuids: [],
  });

  const [isFileLoading, setIsFileLoading] = React.useState(false);

  const handleRatingChange = (event, newValue) => {
    setEntryData((prevState) => ({
      ...prevState,
      value_float: newValue,
    }));
  };

  const handleDeleteFile = (e) => {
    setEntryData((prevState) => ({
      ...prevState,
      file_upload_uuids: [],
    }));
  };

  const uploadFile = async (media, type) => {
    const formData = new FormData();
    formData.append("company_id", props.homeCompany);
    formData.append("file_name", media.name);
    formData.append("type", type);

    setIsFileLoading(true);

    const axiosConfig = await getDefaultAxiosConfig();
    const res = await axios.post(
      API_post_file_upload_with_eagerly_generated_presigned_url,
      formData,
      {
        headers: {
          ...axiosConfig.headers,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    const fileUUID = res?.data?.redux?.payload?.uuid;
    const presignedUrl = res?.data?.redux?.payload?.presigned_url;

    await axios.put(presignedUrl, media, {
      headers: {
        "Content-Type": media.type,
      },
    });

    setIsFileLoading(false);

    return fileUUID;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    const fileUUID = await uploadFile(file, "other");
    setEntryData((prevEntryData) => ({
      ...prevEntryData,
      file_upload_uuids: [fileUUID],
    }));
  };

  const convertEmptyToNull = (value) => {
    return value === "" ? null : value;
  };

  const hasEntryContent =
    entryData.value_text.trim().length > 0 ||
    entryData.value_float !== "" ||
    entryData.value_integer !== "" ||
    entryData.file_upload_uuids.length > 0;

  const handleSave = () => {
    const sanitizedValueFloat = entryData.value_float
      ? typeof entryData.value_float === "string"
        ? entryData.value_float.replace(/,/g, "")
        : entryData.value_float
      : null;
    const sanitizedValueInteger = entryData.value_integer
      ? entryData.value_integer.replace(/,/g, "")
      : null;
    props.post_logbook_category_entry(
      props.category.uuid,
      entryData.value_text,
      convertEmptyToNull(sanitizedValueInteger),
      convertEmptyToNull(sanitizedValueFloat),
      entryData.file_upload_uuids,
      props.entry?.uuid,
      props.categoryType
    );
    setEntryData({
      value_text: "",
      value_integer: "",
      value_float: "",
      file_upload_uuids: [],
    });
    props.setShowInput(false);
  };

  // Styling mirrors BlockEntryComposer (the entry input on metric/table/
  // agent blocks) so entries compose identically everywhere; this one keeps
  // its extra powers: typed values and the photo attachment.
  return (
    <div className="w-full">
      {/* Star Rating */}
      {props.categoryType === "star" && (
        <div className="mb-1.5">
          <Rating
            value={Number(entryData.value_float) || 0}
            onChange={handleRatingChange}
          />
        </div>
      )}

      {/* Numeric Input */}
      {(props.categoryType === "dollar" ||
        props.categoryType === "percentage" ||
        props.categoryType === "number") && (
        <div className="w-32 mb-1.5 flex items-center bg-white border border-slate-200 rounded-lg px-2.5 py-2 focus-within:border-violet-500">
          {props.categoryType === "dollar" && (
            <span className="text-sm text-slate-400 mr-1">$</span>
          )}
          <input
            className="w-full min-w-0 text-sm text-slate-800 focus:outline-none"
            inputMode="decimal"
            placeholder={
              props.categoryType === "dollar"
                ? "1000"
                : props.categoryType === "percentage"
                ? "32.5"
                : "15"
            }
            value={entryData.value_float}
            onChange={(e) =>
              setEntryData((prevState) => ({
                ...prevState,
                value_float: e.target.value,
              }))
            }
          />
          {props.categoryType === "percentage" && (
            <span className="text-sm text-slate-400 ml-1">%</span>
          )}
        </div>
      )}

      {/* Text Input */}
      <AutosizingTextarea
        className="w-full bg-white text-sm text-slate-800 border border-slate-200 rounded-lg px-2.5 py-2 focus:outline-none focus:border-violet-500 resize-none"
        placeholder={
          props.categoryType === "text"
            ? "Add an entry..."
            : "Add a note (optional)..."
        }
        value={entryData.value_text}
        onChange={(e) =>
          setEntryData((prevState) => ({
            ...prevState,
            value_text: e.target.value,
          }))
        }
      />

      {/* Buttons left (matching the block composer), attachment right */}
      <div className="mt-1.5 w-full flex items-center gap-2">
        <button
          className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
            !isFileLoading && hasEntryContent
              ? "bg-violet-700 text-white cursor-pointer hover:bg-violet-800"
              : "bg-slate-100 text-slate-400 cursor-not-allowed"
          }`}
          disabled={isFileLoading || !hasEntryContent}
          onClick={handleSave}
        >
          {isEditMode ? "Save" : "Save entry"}
        </button>
        {isEditMode && (
          <button
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 border border-slate-200 hover:bg-slate-50"
            onClick={() => props.setShowInput(false)}
          >
            Cancel
          </button>
        )}

        {/* Attachment (hidden in edit mode, attachments stay as they are) */}
        <div className="ml-auto">
          {isEditMode ? null : entryData.file_upload_uuids?.length === 0 ? (
            <div>
              <label
                htmlFor={`file-upload-${props.fileKey}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg cursor-pointer text-slate-500 hover:bg-slate-50"
              >
                {!isFileLoading ? (
                  <>
                    <AttachFileIcon style={{ fontSize: "14px" }} />
                    <span className="text-xs font-medium">Add photo</span>
                  </>
                ) : (
                  <CircularProgress size="0.85rem" sx={{ color: "#6d28d9" }} />
                )}
              </label>
              <input
                id={`file-upload-${props.fileKey}`}
                name={`file-upload-${props.fileKey}`}
                type="file"
                className="hidden"
                onChange={handleFileChange}
                accept=".jpg, .jpeg, .png"
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div
                className="w-6 h-6 rounded-md bg-slate-100 flex items-center justify-center cursor-pointer hover:bg-slate-200 transition-colors"
                onClick={() => handleDeleteFile()}
              >
                <CloseIcon style={{ fontSize: "12px", color: "#64748B" }} />
              </div>
              <div
                className="flex items-center gap-1.5 cursor-pointer"
                onClick={() =>
                  props.get_file_upload(entryData?.file_upload_uuids?.[0])
                }
              >
                <ImageIcon style={{ fontSize: "14px", color: "#6d28d9" }} />
                <span className="text-xs font-medium text-violet-700">
                  1 image attached
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InputEntry;
